import { useState, useMemo, useEffect } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { getFirmSetting, setFirmSetting } from './utils/firmSettings';
import { subscribeFirmSettings } from './utils/firmSettingsStore';
import { ClientsProvider } from './context/ClientsContext';
import { HouseholdDetailProvider } from './context/HouseholdDetailContext';
import { FirmSettingsProvider } from './context/FirmSettingsContext';
import { FinancialDataProvider } from './context/FinancialDataContext';
import { PipelineProvider } from './context/PipelineContext';
import { ContentProvider } from './context/ContentContext';
import MigrationBanner from './components/common/MigrationBanner';
import FirmSettingsErrorBanner from './components/common/FirmSettingsErrorBanner';
import ErrorBoundary from './components/common/ErrorBoundary';
import Dashboard from './components/Dashboard/Dashboard';
import PLView from './components/PLView/PLView';
import ScenarioPlanner from './components/ScenarioPlanner/ScenarioPlanner';
import CashFlow from './components/CashFlow/CashFlow';
import PipelineForecast from './components/PipelineForecast/PipelineForecast';
import BWMClients from './components/BWMClients/BWMClients';
import HouseholdPage from './components/Households/HouseholdPage';
import FirmSection from './sections/Firm/FirmSection';
import ProfitFirst from './sections/ProfitFirst/ProfitFirst';
import CRMSection from './sections/CRM/CRMSection';
import DiscoveryCallView from './sections/CRM/DiscoveryCallView';
import StrategyMeetingView from './sections/CRM/StrategyMeetingView';
import EmailFollowUpView from './sections/CRM/EmailFollowUpView';
import InsuranceSection from './sections/Insurance/InsuranceSection';
import { PoliciesRoute, RenewalsRoute, QuotesRoute } from './sections/Insurance/routes';
import MarketingSection from './sections/Marketing/MarketingSection';
import MarketingDashboardView from './sections/Marketing/dashboard/DashboardView';
import ContentView from './sections/Marketing/screens/ContentView';
import SocialView from './sections/Marketing/screens/SocialView';
import ProjectsView from './sections/Marketing/screens/ProjectsView';
import PlanningView from './sections/Marketing/screens/PlanningView';
import AttributionView from './sections/Marketing/screens/AttributionView';
import EmailView from './sections/Marketing/screens/EmailView';
import SeoView from './sections/Marketing/SeoView';
import ContentLogView from './sections/Marketing/ContentLogView';
import BudgetView from './sections/Marketing/BudgetView';
import ReportsSection from './sections/Reports/ReportsSection';
import MonthlyReport from './sections/Reports/MonthlyReport';
import UTMBuilderView from './sections/Marketing/UTMBuilderView';
import { computeMonthlyImpact } from './utils/calculations';
import { useAuth } from './context/AuthContext';
import QBSyncButton from './components/common/QBSyncButton';
import QBCacheHydrator from './components/common/QBCacheHydrator';
import SFSyncButton from './components/common/SFSyncButton';
import ScenarioBadge from './components/common/ScenarioBadge';
import scenarioSeed from './data/scenarios.json';

// Top-level sections. Each gets its own route subtree with optional sub-nav.
const SECTIONS = [
  { to: '/firm',      label: 'Firm' },
  { to: '/crm',       label: 'CRM' },
  { to: '/insurance', label: 'Insurance' },
  { to: '/marketing', label: 'Marketing' },
  { to: '/reports',   label: 'Reports' },
];

export default function App() {
  const { user, signOut } = useAuth();

  // Scenario state — source of truth is firm_settings (Supabase). We seed
  // from cache (synced by FirmSettingsProvider) on mount, then subscribe
  // for re-hydration when the async load completes.
  const [scenarios, setScenarios] = useState(() => {
    const fromSettings = getFirmSetting('scenarios', null);
    if (Array.isArray(fromSettings) && fromSettings.length > 0) return fromSettings;
    try {
      const stored = localStorage.getItem('bcc_scenarios');
      if (stored) { const parsed = JSON.parse(stored); if (Array.isArray(parsed) && parsed.length > 0) return parsed; }
    } catch (e) { /* ignore */ }
    return scenarioSeed.scenarios;
  });
  const [profitFloorPct, setProfitFloorPct] = useState(() => {
    const fromSettings = getFirmSetting('scenarioFloor', null);
    if (typeof fromSettings === 'number') return fromSettings;
    try {
      const stored = localStorage.getItem('bcc_scenario_floor');
      if (stored) return parseFloat(stored);
    } catch (e) { /* ignore */ }
    return scenarioSeed.profitFloorPct;
  });

  useEffect(() => {
    return subscribeFirmSettings((fs) => {
      if (Array.isArray(fs.scenarios)) setScenarios(fs.scenarios);
      if (typeof fs.scenarioFloor === 'number') setProfitFloorPct(fs.scenarioFloor);
    });
  }, []);

  useEffect(() => {
    setFirmSetting('scenarios', scenarios);
    try { localStorage.setItem('bcc_scenarios', JSON.stringify(scenarios)); } catch (e) { /* ignore */ }
  }, [scenarios]);
  useEffect(() => {
    setFirmSetting('scenarioFloor', profitFloorPct);
    try { localStorage.setItem('bcc_scenario_floor', String(profitFloorPct)); } catch (e) { /* ignore */ }
  }, [profitFloorPct]);

  // Cumulative monthly impact from all active scenarios
  const monthlyImpact = useMemo(() => computeMonthlyImpact(scenarios), [scenarios]);

  // Wrap any route element in an ErrorBoundary keyed by route name so a
  // crash in one view doesn't blank the whole app.
  const guard = (label, el) => <ErrorBoundary key={label} label={label}>{el}</ErrorBoundary>;

  return (
    <FirmSettingsProvider>
    <ClientsProvider>
    <HouseholdDetailProvider>
    <FinancialDataProvider>
    <PipelineProvider>
    <ContentProvider>
    <div className="min-h-screen bg-bone">
      <FirmSettingsErrorBanner />
      {/* Pulls shared QB cache into every user's localStorage on mount.
          Lives outside the Adam-only QBSyncButton render so non-admins
          (Robert et al) also get the latest QB data. */}
      <QBCacheHydrator />
      {/* Header */}
      <header className="bg-navy px-6 py-4 flex items-center justify-between">
        <h1 className="text-white text-[20px] font-bold tracking-wide">
          Bullogic Command Center
        </h1>
        {user && (
          <div className="flex items-center gap-3">
            <ScenarioBadge />
            {user.email === 'adam@bullogicwealth.com' && (
              <>
                <QBSyncButton />
                <SFSyncButton />
              </>
            )}
            <span className="text-white/70 text-[12px]">{user.email}</span>
            <button
              onClick={signOut}
              className="text-white/50 hover:text-white text-[12px] font-bold px-3 py-1 border border-white/20 rounded-[4px] hover:border-white/40 transition-colors"
            >
              Sign Out
            </button>
          </div>
        )}
      </header>

      {/* Section Navigation */}
      <nav className="bg-white border-b border-gray-200 px-6">
        <div className="flex items-stretch justify-between">
          <div className="flex gap-0">
            {SECTIONS.map((section) => (
              <NavLink
                key={section.to}
                to={section.to}
                className={({ isActive }) =>
                  `px-5 py-3 text-[13px] font-bold border-b-2 transition-colors ${
                    isActive
                      ? 'text-navy border-bullogic-blue'
                      : 'text-gray-400 border-transparent hover:text-gray-600'
                  }`
                }
              >
                {section.label}
              </NavLink>
            ))}
          </div>
          {/* External deep-link into the client portal admin queue. Sits on
              the same line as the section nav so staff can jump between BCC
              (internal ops) and the portal admin (client-facing ops). */}
          <a
            href={(import.meta.env?.VITE_PORTAL_BASE_URL || 'https://clients.bullogic.app') + '/admin/queue'}
            target="_blank"
            rel="noreferrer"
            className="px-5 py-3 text-[13px] font-bold text-bullogic-blue hover:text-navy border-b-2 border-transparent hover:border-bullogic-blue transition-colors inline-flex items-center gap-1.5"
            title="Open the client portal admin in a new tab"
          >
            Client Portal
            <span className="text-[10px] opacity-70">↗</span>
          </a>
        </div>
      </nav>

      {/* Main Content */}
      <main className="px-6 py-6 max-w-[1800px] mx-auto">
        <MigrationBanner />
        <Routes>
          {/* Default: redirect to Firm */}
          <Route path="/" element={<Navigate to="/firm" replace />} />

          {/* Firm: Dashboard, P&L, Scenarios, Cash Flow */}
          <Route path="/firm" element={<FirmSection />}>
            <Route index element={guard('dashboard', <Dashboard monthlyImpact={monthlyImpact} />)} />
            <Route path="pl" element={guard('pl-view', <PLView scenarioImpact={monthlyImpact} />)} />
            <Route path="scenarios" element={guard('scenario', (
              <ScenarioPlanner
                scenarios={scenarios}
                setScenarios={setScenarios}
                profitFloorPct={profitFloorPct}
                setProfitFloorPct={setProfitFloorPct}
                monthlyImpact={monthlyImpact}
              />
            ))} />
            <Route path="cash-flow" element={guard('cash-flow', <CashFlow monthlyImpact={monthlyImpact} />)} />
            <Route path="profit-first" element={guard('profit-first', <ProfitFirst />)} />
          </Route>

          {/* CRM: Pipeline & Forecast (default), Households, per-prospect meeting notes */}
          <Route path="/crm" element={<CRMSection />}>
            <Route index element={guard('pipeline-forecast', <PipelineForecast />)} />
            <Route path="households" element={guard('households', <BWMClients />)} />
            {/* Per-household detail page (Phase B of Households redesign).
                Sub-routes per tab are matched by HouseholdPage's own
                <Routes>. The /* wildcard lets nested matching work. */}
            <Route path="households/:id/*" element={guard('households', <HouseholdPage />)} />
            <Route path="prospects/:id/discovery" element={guard('discovery-call', <DiscoveryCallView />)} />
            <Route path="prospects/:id/strategy" element={guard('strategy-meeting', <StrategyMeetingView />)} />
            <Route path="prospects/:id/follow-up" element={guard('email-followup', <EmailFollowUpView />)} />
          </Route>

          {/* Insurance: dashboard widget always visible; sub-routes for
              Policies (default) / Renewals / Quotes. */}
          <Route path="/insurance" element={guard('insurance', <InsuranceSection />)}>
            <Route index element={<Navigate to="policies" replace />} />
            <Route path="policies" element={<PoliciesRoute />} />
            <Route path="renewals" element={<RenewalsRoute />} />
            <Route path="quotes" element={<QuotesRoute />} />
          </Route>

          {/* Marketing: redesigned workspace (MARKETING_REBUILD_HANDOFF.md).
              Run screens (Dashboard/Content/Social/Projects), Measure screens
              (Attribution/Email/SEO/Budget), plus transitional Tools routes
              that keep the old utilities reachable until later phases. */}
          <Route path="/marketing" element={<MarketingSection />}>
            <Route index element={guard('marketing', <MarketingDashboardView />)} />
            <Route path="content" element={guard('marketing-content', <ContentView />)} />
            <Route path="social" element={guard('marketing-social', <SocialView />)} />
            <Route path="projects" element={guard('marketing-projects', <ProjectsView />)} />
            <Route path="planning" element={guard('marketing-planning', <PlanningView />)} />
            <Route path="attribution" element={guard('marketing-attribution', <AttributionView />)} />
            <Route path="email" element={guard('marketing-email', <EmailView />)} />
            <Route path="seo" element={guard('marketing-seo', <SeoView />)} />
            <Route path="budget" element={guard('marketing-budget', <BudgetView />)} />
            <Route path="content-log" element={guard('marketing-content-log', <ContentLogView />)} />
            <Route path="utm" element={guard('marketing-utm', <UTMBuilderView />)} />
            {/* Retired tabs redirect to their replacements */}
            <Route path="linkedin" element={<Navigate to="/marketing/social" replace />} />
            <Route path="strategies" element={<Navigate to="/marketing/planning" replace />} />
            <Route path="avatars" element={<Navigate to="/marketing/planning" replace />} />
          </Route>

          {/* Reports: cross-cutting summaries */}
          <Route path="/reports" element={<ReportsSection />}>
            <Route index element={<Navigate to="monthly" replace />} />
            <Route path="monthly" element={guard('reports-monthly', <MonthlyReport />)} />
          </Route>

          {/* Catch-all: anything else 404s back to Firm */}
          <Route path="*" element={<Navigate to="/firm" replace />} />
        </Routes>
      </main>
    </div>
    </ContentProvider>
    </PipelineProvider>
    </FinancialDataProvider>
    </HouseholdDetailProvider>
    </ClientsProvider>
    </FirmSettingsProvider>
  );
}
